import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { pb } from '../../lib/pocketbase';
import { useNavigate } from 'react-router-dom';
import { LogOut, Users, Settings2, Save, FileText, Download } from 'lucide-react';
import html2pdf from 'html2pdf.js';

interface Partner {
    id: string;
    email: string;
    name: string;
    isMainPartner: boolean;
    affiliateRate: number;
    stripeAccountId: string;
    totalReferrals: number;
    billingAddress?: string;
    taxId?: string;
}

interface Referral {
    id: string;
    affiliateId: string;
    amount: number;
    status: string;
    created: string;
}

interface PartnerSummary {
    partner: Partner;
    referrals: Referral[];
    totalAmount: number;
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [partners, setPartners] = useState<Partner[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [savingId, setSavingId] = useState<string | null>(null);

    // Tab State
    const [activeTab, setActiveTab] = useState<'partners' | 'billing'>('partners');

    // Billing State
    const [referrals, setReferrals] = useState<Referral[]>([]);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [pdfGeneratingId, setPdfGeneratingId] = useState<string | null>(null);

    useEffect(() => {
        // Protect route: Ensure there is an active Admin token
        if (!pb.authStore.isValid || !pb.authStore.isAdmin) {
            navigate('/admin-login');
            return;
        }

        const init = async () => {
            setIsLoading(true);
            await fetchPartners();
            setIsLoading(false);
        };
        init();
    }, [navigate]);

    // Fetch referrals whenever tab switches to billing or selected month changes
    useEffect(() => {
        if (activeTab === 'billing' && pb.authStore.isValid && pb.authStore.isAdmin) {
            fetchReferrals();
        }
    }, [activeTab, selectedMonth]);

    const fetchPartners = async () => {
        try {
            // As an admin, we can fetch all records
            const records = await pb.collection('users').getFullList<Partner>({
                sort: '-created',
            });
            setPartners(records);
        } catch (error) {
            console.error("Fehler beim Laden der Partner:", error);
        }
    };

    const fetchReferrals = async () => {
        try {
            // Calculate start and end dates for the selected month
            const [year, month] = selectedMonth.split('-');
            const startDate = new Date(Number(year), Number(month) - 1, 1).toISOString();
            const endDate = new Date(Number(year), Number(month), 1).toISOString();

            const records = await pb.collection('referrals').getFullList<Referral>({
                filter: `created >= "${startDate}" && created < "${endDate}"`,
                sort: '-created',
            });
            setReferrals(records);
        } catch (error) {
            console.error("Fehler beim Laden der Referrals:", error);
        }
    };

    // Derived State: Summarize referrals by partner
    const summaries: PartnerSummary[] = partners.map(partner => {
        const partnerReferrals = referrals.filter(r => r.affiliateId === partner.id);
        const totalAmount = partnerReferrals.reduce((sum, r) => sum + r.amount, 0);
        return { partner, referrals: partnerReferrals, totalAmount };
    }).filter(summary => summary.totalAmount > 0); // Only show partners with earnings this month


    const handleLogout = () => {
        pb.authStore.clear();
        navigate('/admin-login');
    };

    const updatePartner = async (id: string, field: string, value: any) => {
        setSavingId(id);
        try {
            // Update the state optimistically
            setPartners(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));

            // Save to PocketBase
            await pb.collection('users').update(id, {
                [field]: value
            });
        } catch (err) {
            console.error("Fehler beim Speichern:", err);
            // Re-fetch to revert to actual state on error
            fetchPartners();
        } finally {
            setTimeout(() => setSavingId(null), 500); // small delay to show the saving icon
        }
    };

    const generatePDF = async (summary: PartnerSummary) => {
        setPdfGeneratingId(summary.partner.id);
        try {
            const [year, month] = selectedMonth.split('-');
            const invoiceNumber = `GS-${year}-${month}-${summary.partner.id.substring(0, 5).toUpperCase()}`;
            const dateStr = new Date().toLocaleDateString('de-DE');

            // Format billing address (handle potential newlines)
            const billingAddressLines = summary.partner.billingAddress
                ? summary.partner.billingAddress.split('\n').map(line => `${line}<br/>`).join('')
                : 'Keine Adresse hinterlegt';

            // PDF Template HTML
            const htmlContent = `
            <div style="font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; font-size: 14px; position: relative; min-height: 100vh;">
                <!-- Header -->
                <div style="display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 20px; margin-bottom: 40px;">
                    <div>
                        <h1 style="margin: 0; font-size: 28px; font-weight: bold; color: #000;">Reputrust</h1>
                        <p style="margin: 5px 0 0; font-size: 12px; color: #666;">Ein Service der SMC Simon Müller Consulting e.K.</p>
                    </div>
                </div>

                <!-- Addresses -->
                <div style="display: flex; justify-content: space-between; margin-bottom: 40px;">
                    <div style="width: 45%;">
                        <p style="font-size: 10px; color: #666; text-decoration: underline; margin-bottom: 10px;">
                            SMC e.K. - Bissinger Str. 27 - 73252 Lenningen
                        </p>
                        <p style="margin: 0;">
                            <strong>${summary.partner.name || summary.partner.email}</strong><br/>
                            ${billingAddressLines}
                        </p>
                        ${summary.partner.taxId ? `<p style="margin: 5px 0 0;">USt-IdNr.: ${summary.partner.taxId}</p>` : ''}
                    </div>
                    <div style="width: 45%; text-align: right;">
                        <p style="margin: 0;"><strong>Datum:</strong> ${dateStr}</p>
                        <p style="margin: 5px 0 0;"><strong>Gutschrift Nr.:</strong> ${invoiceNumber}</p>
                        <p style="margin: 5px 0 0;"><strong>Leistungszeitraum:</strong> ${month}/${year}</p>
                    </div>
                </div>

                <!-- Subject -->
                <h2 style="font-size: 18px; margin-bottom: 30px;">Gutschrift für Vermittlungsprovisionen (${month}/${year})</h2>
                <p style="margin-bottom: 30px;">
                    Sehr geehrte(r) Herr/Frau ${summary.partner.name || 'Partner'},<br/><br/>
                    vereinbarungsgemäß schreiben wir Ihnen für die erfolgreiche Vermittlung von Reputrust-Abonnements im vergangenen Monat folgenden Betrag gut:
                </p>

                <!-- Table -->
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
                    <thead>
                        <tr style="border-bottom: 1px solid #ccc;">
                            <th style="text-align: left; padding: 10px 5px;">Pos.</th>
                            <th style="text-align: left; padding: 10px 5px;">Beschreibung</th>
                            <th style="text-align: right; padding: 10px 5px;">Anzahl</th>
                            <th style="text-align: right; padding: 10px 5px;">Gesamtbetrag</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style="padding: 10px 5px; border-bottom: 1px solid #eee;">1</td>
                            <td style="padding: 10px 5px; border-bottom: 1px solid #eee;">Vermittlungsprovision (Abonnements)</td>
                            <td style="padding: 10px 5px; border-bottom: 1px solid #eee; text-align: right;">${summary.referrals.length}</td>
                            <td style="padding: 10px 5px; border-bottom: 1px solid #eee; text-align: right;">${(summary.totalAmount / 100).toFixed(2).replace('.', ',')} €</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colspan="3" style="text-align: right; padding: 15px 5px; font-weight: bold; border-top: 2px solid #000;">Gutschriftsbetrag (Netto)*</td>
                            <td style="text-align: right; padding: 15px 5px; font-weight: bold; border-top: 2px solid #000;">${(summary.totalAmount / 100).toFixed(2).replace('.', ',')} €</td>
                        </tr>
                    </tfoot>
                </table>

                <p style="font-size: 12px; color: #666; margin-bottom: 40px;">
                    *Hinweis: Die Auszahlung dieses Betrages erfolgte bereits automatisch auf das von Ihnen hinterlegte Bankkonto (via Stripe Connect).<br/>
                    <em>Es handelt sich um eine Netto-Gutschrift. Sofern Sie umsatzsteuerpflichtig sind, sind Sie zur Versteuerung selbst verpflichtet.</em>
                </p>

                <!-- Footer (Positioned at bottom) -->
                <div style="position: absolute; bottom: 40px; left: 40px; right: 40px; font-size: 10px; color: #666; border-top: 1px solid #ccc; padding-top: 15px; display: flex; justify-content: space-between;">
                    <div style="width: 33%;">
                        <strong>SMC Simon Müller Consulting e.K.</strong><br/>
                        Bissinger Str. 27<br/>
                        73252 Lenningen<br/>
                        Deutschland
                    </div>
                    <div style="width: 33%;">
                        <strong>Kontakt</strong><br/>
                        Email: info@reputrust.ai<br/>
                        Web: www.reputrust.ai
                    </div>
                    <div style="width: 33%;">
                        <strong>Rechtliches</strong><br/>
                        USt-IdNr.: DE350766669<br/>
                        Amtsgericht Stuttgart, HRA 738663<br/>
                        Inhaber: Simon Müller
                    </div>
                </div>
            </div>
            `;

            // Create a temporary container
            const opt = {
                margin: 0,
                filename: `Gutschrift_${invoiceNumber}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
            };

            await html2pdf().from(htmlContent).set(opt).save();

        } catch (err) {
            console.error("Fehler bei PDF Generierung:", err);
            alert("Fehler bei der PDF Generierung.");
        } finally {
            setPdfGeneratingId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <p className="text-slate-500 font-medium">Lade Dashboard...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 font-sans pb-12">
            {/* Header */}
            <header className="bg-[#0F172A] border-b border-slate-800 sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-800 rounded-lg">
                                <Settings2 className="w-5 h-5 text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]" />
                            </div>
                            <h1 className="text-xl font-bold text-white tracking-tight">Super-Admin Panel</h1>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            Abmelden
                        </button>
                    </div>
                </div>
            </header>

            {/* Navigation Tabs */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('partners')}
                            className={`${activeTab === 'partners'
                                ? 'border-tron-primary text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]'
                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors`}
                        >
                            <Users className="w-4 h-4" />
                            Partner Manager
                        </button>
                        <button
                            onClick={() => setActiveTab('billing')}
                            className={`${activeTab === 'billing'
                                ? 'border-tron-primary text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]'
                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors`}
                        >
                            <FileText className="w-4 h-4" />
                            Abrechnungen (Gutschriften)
                        </button>
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">

                {activeTab === 'partners' && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                        <div className="mb-6 flex justify-between items-end">
                            <div>
                                <h2 className="text-2xl font-bold text-[#0F172A]">Partner Manager</h2>
                                <p className="mt-1 text-sm text-slate-500">Verwalte Provisionssätze und Main Partner Zuweisungen.</p>
                            </div>
                            <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-200 flex items-center gap-2">
                                <Users className="w-4 h-4 text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]" />
                                <span className="font-semibold text-slate-700">{partners.length} Partner</span>
                            </div>
                        </div>

                        <div className="bg-white shadow-sm border border-slate-200 rounded-2xl overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-200">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Partner Info
                                            </th>
                                            <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Stripe Status
                                            </th>
                                            <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Provisionssatz (Dezimal)
                                            </th>
                                            <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Main Partner Flag
                                            </th>
                                            <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-slate-100">
                                        {partners.map((partner) => (
                                            <tr key={partner.id} className="hover:bg-slate-50/50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-semibold text-[#0F172A]">{partner.name || 'Kein Name'}</span>
                                                        <span className="text-sm text-slate-500">{partner.email}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {partner.stripeAccountId ? (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div> Connected
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-tron-secondary border border-emerald-200">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-tron-secondary"></div> Missing
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max="1"
                                                                value={partner.affiliateRate || 0}
                                                                onChange={(e) => updatePartner(partner.id, 'affiliateRate', parseFloat(e.target.value))}
                                                                className="w-24 px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900"
                                                            />
                                                            <span className="text-sm font-medium text-slate-600">
                                                                ({((partner.affiliateRate || 0) * 100).toFixed(0)}%)
                                                            </span>
                                                        </div>
                                                        <span className="text-xs text-slate-400 mt-1">z.B. 0.2 für 20%</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={partner.isMainPartner || false}
                                                            onChange={(e) => updatePartner(partner.id, 'isMainPartner', e.target.checked)}
                                                        />
                                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r from-[#10B981] to-[#1E3A8A]"></div>
                                                        <span className="ml-3 text-sm font-medium text-slate-700">
                                                            {partner.isMainPartner ? '50% Main Partner' : 'Nein'}
                                                        </span>
                                                    </label>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                                    {savingId === partner.id ? (
                                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-end items-center gap-1.5 text-green-600 font-medium text-sm">
                                                            <Save className="w-4 h-4" /> Gespeichert
                                                        </motion.div>
                                                    ) : null}
                                                </td>
                                            </tr>
                                        ))}
                                        {partners.length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-8 text-center text-sm text-slate-500">
                                                    Noch keine Partner registriert.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </motion.div>
                )}
            </main>
        </div>
    );
}
